import type { UnitCode } from '~/graphql/graphql';

export type BundleDimensions = {
  isNominal?: boolean | null;
  length: {
    actualLengthInches: number;
    nominalLengthInches?: number | null;
  };
  platformProduct: {
    nominalWidthInches?: number | null;
    actualWidthInches?: number | null;
    nominalThicknessInches?: number | null;
    actualThicknessInches?: number | null;
    actualLengthInches?: number | null;
    piecesPerBundle: { value: number; unit: UnitCode };
  };
};

export type GroupableItem = {
  quantityInPieces: number;
  piecesPerBundle?: number | null;
  platformProductsLength: BundleDimensions & {
    id: string;
    length: { id: string; displayValue: string };
    platformProduct: {
      id: string;
      name: string;
      code?: string | null;
      standardUnitsCode: UnitCode;
    };
  };
};

export type ItemRow = {
  platformProductLengthId: string;
  lengthId: string;
  displayValue: string;
  actualLengthInches: number;
  piecesPerBundle: number;
  requestedQuantityInPieces: number | null;
  bundleDimensions?: BundleDimensions;
};

export type ProductGroup = {
  productId: string;
  productName: string;
  productCode: string | null;
  standardUnitsCode: UnitCode;
  piecesPerBundle: number;
  rows: ItemRow[];
};

type MutableGroup = Omit<ProductGroup, 'rows'> & {
  rowsByPlatformProductLengthId: Map<string, ItemRow>;
};

export function byAscendingActualLength(left: ItemRow, right: ItemRow): number {
  return left.actualLengthInches - right.actualLengthInches;
}

function rowFor(item: GroupableItem, requested: boolean): ItemRow {
  const { platformProductsLength } = item;
  const productPiecesPerBundle =
    platformProductsLength.platformProduct.piecesPerBundle.value;
  return {
    platformProductLengthId: platformProductsLength.id,
    lengthId: platformProductsLength.length.id,
    displayValue: platformProductsLength.length.displayValue,
    actualLengthInches: platformProductsLength.length.actualLengthInches,
    // An offered-only row is shared by every seller who quoted it, so it takes the
    // product default rather than whichever seller was flattened into the list first.
    piecesPerBundle: requested
      ? (item.piecesPerBundle ?? productPiecesPerBundle)
      : productPiecesPerBundle,
    requestedQuantityInPieces: requested ? item.quantityInPieces : null,
    bundleDimensions: platformProductsLength,
  };
}

function groupFor(
  groups: Map<string, MutableGroup>,
  item: GroupableItem
): MutableGroup {
  const product = item.platformProductsLength.platformProduct;
  const existing = groups.get(product.id);
  if (existing) return existing;

  const created: MutableGroup = {
    productId: product.id,
    productName: product.name,
    productCode: product.code ?? null,
    standardUnitsCode: product.standardUnitsCode,
    piecesPerBundle: product.piecesPerBundle.value,
    rowsByPlatformProductLengthId: new Map(),
  };
  groups.set(product.id, created);
  return created;
}

/**
 * The item model for the request-for-quote page: platform products in name order, each
 * holding its lengths in ascending order.
 *
 * Requested items set the requested quantity. A length or product that only a seller
 * offered still gets a row, with a null requested quantity, so the buyer sees what was
 * offered beyond what they asked for.
 *
 * @param requestedItems the request for quote's own items
 * @param offeredItems every quote item across every quote on the request
 *
 * @example
 * const groups = buildProductGroups(requestForQuote.items, quotes.flatMap((q) => q.items));
 */
export function buildProductGroups(
  requestedItems: GroupableItem[],
  offeredItems: GroupableItem[]
): ProductGroup[] {
  const groups = new Map<string, MutableGroup>();

  for (const item of requestedItems) {
    const group = groupFor(groups, item);
    group.rowsByPlatformProductLengthId.set(
      item.platformProductsLength.id,
      rowFor(item, true)
    );
  }

  for (const item of offeredItems) {
    const group = groupFor(groups, item);
    if (
      !group.rowsByPlatformProductLengthId.has(item.platformProductsLength.id)
    ) {
      group.rowsByPlatformProductLengthId.set(
        item.platformProductsLength.id,
        rowFor(item, false)
      );
    }
  }

  return [...groups.values()]
    .map(({ rowsByPlatformProductLengthId, ...group }) => ({
      ...group,
      rows: [...rowsByPlatformProductLengthId.values()].sort(
        byAscendingActualLength
      ),
    }))
    .sort((left, right) => left.productName.localeCompare(right.productName));
}
